// src/components/sprint/CountdownTimer.tsx

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CountdownTimerProps {
  targetDate: string;
}

const calculateTimeLeft = (targetDate: string) => {
  const difference = +new Date(targetDate) - +new Date();
  let timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 };

  if (difference > 0) {
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }
  return timeLeft;
};

const CountdownSegment: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="relative h-24 w-20 flex items-center justify-center">
      <AnimatePresence mode="popLayout">
        <motion.span
          key={value}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ ease: 'easeInOut', duration: 0.3 }}
          className="absolute text-6xl md:text-7xl font-extrabold text-white"
        >
          {String(value).padStart(2, '0')}
        </motion.span>
      </AnimatePresence>
    </div>
    <span className="text-sm font-semibold text-slate-300 uppercase tracking-wider">{label}</span>
  </div>
);

export const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex items-center justify-center gap-4 md:gap-8">
      <CountdownSegment value={timeLeft.days} label="Days" />
      <span className="text-5xl font-light text-slate-500 pb-6">:</span>
      <CountdownSegment value={timeLeft.hours} label="Hours" />
      <span className="text-5xl font-light text-slate-500 pb-6">:</span>
      <CountdownSegment value={timeLeft.minutes} label="Minutes" />
      <span className="text-5xl font-light text-slate-500 pb-6">:</span>
      <CountdownSegment value={timeLeft.seconds} label="Seconds" />
    </div>
  );
};